/**
 * WebSocket Subscription Module
 *
 * This module provides real-time data subscriptions using WebSocket v2 protocol
 * with multiplexed connections (one connection per client, multiple subscriptions).
 *
 * The external API remains unchanged - just use subscribe(path, options) as before.
 */
import { subscribeV2, closeAllSubscriptionsV2, clearCacheV2, getCachedDataV2, reconnectWithNewAuthV2, } from './subscription-v2';
/**
 * Subscribe to real-time updates for a path.
 *
 * @param path - The path to subscribe to (e.g., 'users/123' or 'posts')
 * @param subscriptionOptions - Options including onData and onError callbacks
 * @returns A function to unsubscribe
 *
 * @example
 * ```typescript
 * const unsubscribe = await subscribe('users/123', {
 *   onData: (data) => console.log('User updated:', data),
 *   onError: (error) => console.error('Subscription error:', error),
 * });
 *
 * // Later, to unsubscribe:
 * await unsubscribe();
 * ```
 */
export async function subscribe(path, subscriptionOptions) {
    // Forgive the most natural mistake: `subscribe(path, (data) => ...)`. Without
    // this, a bare callback is taken as the options object, `.onData` is undefined,
    // and the subscription silently never fires (no error) — it just looks broken.
    // Coerce a function into `{ onData }` so the natural form works.
    const opts = typeof subscriptionOptions === 'function'
        ? { onData: subscriptionOptions }
        : subscriptionOptions;
    return subscribeV2(path, opts);
}
/**
 * Close all active subscriptions.
 * Call this when cleaning up (e.g., on logout or component unmount).
 */
export async function closeAllSubscriptions() {
    return closeAllSubscriptionsV2();
}
/**
 * Clear the subscription cache.
 *
 * @param path - Optional path to clear. If not provided, clears all cached data.
 */
export function clearCache(path) {
    clearCacheV2(path);
}
/**
 * Get cached data for a path without making a network request.
 *
 * @param path - The path to get cached data for
 * @param prompt - Optional prompt that was used for the subscription
 * @returns The cached data, or null if not cached or expired
 */
export function getCachedData(path, prompt) {
    return getCachedDataV2(path, prompt);
}
/**
 * Reconnect all WebSocket connections with fresh authentication.
 * Call this when the user's auth state changes (login/logout).
 *
 * This will:
 * 1. Close existing connections
 * 2. Re-establish with new auth token
 * 3. Re-subscribe to all active paths
 *
 * Existing subscriptions will receive new initial data after reconnection.
 */
export async function reconnectWithNewAuth() {
    return reconnectWithNewAuthV2();
}
